import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DateInput } from "./DateInput";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, errMsg, getCompanyContext } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UploadCloud } from "lucide-react";

export function UploadDocDialog({ open, onOpenChange, employeeId, defaultCategory, onUploaded }) {
  const { meta } = useAuth();
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(defaultCategory || "outros");
  const [emp, setEmp] = useState(employeeId || "");
  const [expires, setExpires] = useState("");
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null); setCategory(defaultCategory || "outros"); setEmp(employeeId || ""); setExpires("");
    if (!employeeId) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [open, employeeId, defaultCategory]);

  const send = async () => {
    if (!file) { toast.error("Selecione um arquivo."); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("category", category);
    if (emp && emp !== "none") fd.append("employee_id", emp);
    if (expires) fd.append("expires_at", expires);
    if (getCompanyContext()) fd.append("company_id", getCompanyContext());
    setBusy(true);
    try { const { data } = await api.post("/documents", fd); toast.success(data.version > 1 ? `Nova versão v${data.version} salva — a anterior foi preservada.` : "Documento enviado"); onUploaded && onUploaded(data); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const cats = (meta?.doc_categories || []).filter((c) => !["folha", "encargos", "arquivo_bancario"].includes(c.key));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-doc-dialog" className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Enviar documento</DialogTitle><DialogDescription>Arquivos ficam no dossiê digital com versão, autor, data e hash.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors">
            <UploadCloud className="h-7 w-7 text-indigo-500" />
            <span className="text-sm text-slate-600 text-center px-4">{file ? file.name : "Clique para escolher (PDF, imagem, planilha — até 15 MB)"}</span>
            <input data-testid="upload-doc-file-input" type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <div><Label>Categoria</Label>
            <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="upload-doc-category" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          {!employeeId && (
            <div><Label>Funcionário (opcional)</Label>
              <Select value={emp} onValueChange={setEmp}><SelectTrigger data-testid="upload-doc-employee" className="mt-1.5"><SelectValue placeholder="Documento da empresa" /></SelectTrigger>
                <SelectContent className="max-h-64"><SelectItem value="none">Documento da empresa</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div><Label>Vencimento (opcional)</Label><DateInput testid="upload-doc-expires" className="mt-1.5" value={expires} onChange={setExpires} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button><Button data-testid="upload-doc-submit" onClick={send} disabled={busy}>{busy ? "Enviando..." : "Enviar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
